import type { LiveData } from '@toeverything/infra';
import { Service } from '@toeverything/infra';

import type { WorkspacePropertiesAdapter } from './adapter';
import type { FollowupEmailStatus, WorkspaceAffinePageProperties, WorkspaceAffineProperties } from './schema';

/**
 * FollowupEmailService
 *
 * Manages the per-page Follow-up Email Status property via the WorkspacePropertiesAdapter.
 * Status values:
 * - 'NotStarted' — default state
 * - 'Draft' — email has been drafted (e.g., AI suggested or user started composing)
 * - 'Copied' — user copied the composed email content
 */
export class FollowupEmailService extends Service {
  constructor(private readonly adapter: WorkspacePropertiesAdapter) {
    super();
  }

  /**
   * Reactive stream of the Follow-up Email status for a given page.
   */
  status$(pageId: string): LiveData<FollowupEmailStatus> {
    return this.adapter.properties$.map((props: WorkspaceAffineProperties) => {
      const pageProps = props.pageProperties?.[pageId] as
        | WorkspaceAffinePageProperties
        | undefined;

      const value = pageProps?.system?.followupEmailStatus?.value;
      return this.normalizeStatus(value);
    });
  }

  /**
   * Reactive snapshot of all pages' Follow-up Email statuses, keyed by pageId.
   * Useful for cross-page dashboards or summaries.
   */
  get statuses$(): LiveData<Record<string, FollowupEmailStatus>> {
    return this.adapter.properties$.map((props: WorkspaceAffineProperties) => {
      const entries = Object.entries(
        (props.pageProperties ?? {}) as Record<string, WorkspaceAffinePageProperties>
      );

      const result: Record<string, FollowupEmailStatus> = {};
      for (const [pageId, p] of entries) {
        result[pageId] = this.normalizeStatus(p.system?.followupEmailStatus?.value);
      }
      return result;
    });
  }

  /**
   * Get the current Follow-up Email status for a page (synchronous).
   */
  getStatus(pageId: string): FollowupEmailStatus {
    return this.adapter.getPageFollowupEmailStatus(pageId);
  }

  /**
   * Set the Follow-up Email status for a page.
   */
  setStatus(pageId: string, status: FollowupEmailStatus): void {
    this.adapter.setPageFollowupEmailStatus(pageId, status);
  }

  /**
   * Convenience: mark status as Draft.
   * Do not downgrade if already Copied.
   */
  markDraft(pageId: string): void {
    const current = this.getStatus(pageId);
    if (current !== 'Copied') {
      this.setStatus(pageId, 'Draft');
    }
  }

  /**
   * Convenience: mark status as Copied (user copied email content).
   */
  markCopied(pageId: string): void {
    this.setStatus(pageId, 'Copied');
  }

  /**
   * Convenience: reset status to NotStarted.
   */
  reset(pageId: string): void {
    this.setStatus(pageId, 'NotStarted');
  }

  private normalizeStatus(value: unknown): FollowupEmailStatus {
    return value === 'Draft' || value === 'Copied' || value === 'NotStarted'
      ? value
      : 'NotStarted';
  }
}
